import { getSelectedKeyboardLayoutConfig } from "../../../lib/utils";
import { resolveKeyForLayout } from "./hqShared.js";

const KEYCAP_BG = "#FCBB96";
const KEYCAP_BORDER = "#F0A77D";
// const KEYCAP_BG = "#E6E8EC";
// const KEYCAP_BORDER = "#C7CDD5";
const KEY_STROKE_COLOR = "#A4390B";
const KEY_TEXT_COLOR = "#ffffff";
const LABEL_TEXT_COLOR = "#1a1a1a";

const ICON_BASE = "assets/images/headquarter/custom/";
const PACKAGE_ICON_SRC = "assets/images/packages/package_icon.svg";

const LANE_KEY_CODES = ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"];
const LANE_LABELS = ["dark", "odd", "consonant", "vowel", "even", "bright"];
const LANE_NUMBERS = ["1", "2", "3", "5", "6", "7"];

const SPEED_KEY_STYLE = {
  width: 38,
  height: 68,
  gap: 2,
  iconWidth: 22,
  iconHeight: 22,
  iconOffsetY: -3,
  fontSize: 26,
  strokeWidth: 5,
  letterOffsetY: 6,
};

const LANE_KEY_STYLE = {
  width: 52,
  defaultHeight: 70,
  labeledHeight: 80,
  gap: 2,
  labelHorizontalPadding: 8,
  numberFontSize: 14,
  numberStrokeWidth: 4,
  labelFontSize: 10,
  fontSize: 26,
  keyStrokeWidth: 5,
  letterOffsetY: 6,
};

const SPACEBAR_STYLE = {
  width: 100,
  height: 70,
  gap: 4,
  iconWidth: 64,
  iconHeight: 28,
  labelFontSize: 14,
};

function resolveKey(keyCode, layoutConfig) {
  return resolveKeyForLayout(keyCode, layoutConfig);
}

function buildSpeedKeyCap(type, layoutConfig, options = {}) {
  const keyCode = type === "slowdown" ? "KeyZ" : "Slash";
  const iconFile = type === "slowdown" ? "slowdownIcon.svg" : "speedUpIcon.svg";
  const letter = resolveKey(keyCode, layoutConfig);
  const {
    opacity = 1,
    width = SPEED_KEY_STYLE.width,
    height = SPEED_KEY_STYLE.height,
    gap = SPEED_KEY_STYLE.gap,
    iconWidth = SPEED_KEY_STYLE.iconWidth,
    iconHeight = SPEED_KEY_STYLE.iconHeight,
    iconOffsetY = SPEED_KEY_STYLE.iconOffsetY,
    fontSize = SPEED_KEY_STYLE.fontSize,
    letterOffsetY = SPEED_KEY_STYLE.letterOffsetY,
  } = options;
  const opacityStyle = opacity < 1 ? `opacity:${opacity};` : "";

  return `
    <div style="
      --speed-key-width:${width}px;
      --speed-key-height:${height}px;
      --speed-key-gap:${gap}px;
      --speed-key-icon-width:${iconWidth}px;
      --speed-key-icon-height:${iconHeight}px;
      --speed-key-icon-offset-y:${iconOffsetY}px;
      --speed-key-font-size:${fontSize}px;
      --speed-key-letter-offset-y:${letterOffsetY}px;
      ${opacityStyle}
    " class="lane-labels-keycap lane-labels-speed-key" data-key-code="${keyCode}">
      <img src="${ICON_BASE}${iconFile}" class="lane-labels-speed-key-icon" alt="" />
      <span class="lane-labels-speed-key-label">${letter}</span>
    </div>`;
}

function buildSpaceBarKeyCap(options = {}) {
  const {
    width = SPACEBAR_STYLE.width,
    height = SPACEBAR_STYLE.height,
    gap = SPACEBAR_STYLE.gap,
    iconWidth = SPACEBAR_STYLE.iconWidth,
    iconHeight = SPACEBAR_STYLE.iconHeight,
    labelFontSize = SPACEBAR_STYLE.labelFontSize,
  } = options;

  return `
    <div style="
      --space-key-width:${width}px;
      --space-key-height:${height}px;
      --space-key-gap:${gap}px;
      --space-key-label-font-size:${labelFontSize}px;
      --space-key-icon-width:${iconWidth}px;
      --space-key-icon-height:${iconHeight}px;
    " class="lane-labels-keycap lane-labels-space-key" data-key-code="Space">
      <span class="lane-labels-space-key-label">spacebar</span>
      <img src="${ICON_BASE}spacebarIcon.svg" class="lane-labels-space-key-icon" alt="Space" />
    </div>`;
}

function buildLaneKeyCap(keyCode, opts = {}) {
  const {
    layoutConfig,
    showLabel = false,
    showNumber = false,
    index = 0,
    bw = false,
    opacity = 1,
    width = LANE_KEY_STYLE.width,
    defaultHeight = LANE_KEY_STYLE.defaultHeight,
    labeledHeight = LANE_KEY_STYLE.labeledHeight,
    gap = LANE_KEY_STYLE.gap,
    labelHorizontalPadding = LANE_KEY_STYLE.labelHorizontalPadding,
    numberFontSize = LANE_KEY_STYLE.numberFontSize,
    numberStrokeWidth = LANE_KEY_STYLE.numberStrokeWidth,
    keyFontSize = LANE_KEY_STYLE.fontSize,
    keyStrokeWidth = LANE_KEY_STYLE.keyStrokeWidth,
    labelFontSize = LANE_KEY_STYLE.labelFontSize,
    labelFontWeight = 600,
    labelFontFamily = "'Inter',sans-serif",
    letterOffsetY = LANE_KEY_STYLE.letterOffsetY,
    customLabelHtml = null,
    customLabelWidth = null,
  } = opts;
  const letter = resolveKey(keyCode, layoutConfig);
  const labelText = LANE_LABELS[index] ?? "";
  const laneNumber = LANE_NUMBERS[index] ?? "";

  const strokeColor = bw ? "#555555" : KEY_STROKE_COLOR;
  const textColor = bw ? "#000000" : KEY_TEXT_COLOR;
  const bgColor = bw ? "#d8d8d8" : KEYCAP_BG;
  const borderColor = bw ? "#aaaaaa" : KEYCAP_BORDER;
  const opacityStyle = opacity < 1 ? `opacity:${opacity};` : "";
  const compactLabelKey = showLabel && !showNumber;

  const heightPx = compactLabelKey
    ? labeledHeight - 12
    : showLabel
      ? labeledHeight
      : defaultHeight;
  const renderedKeyFontSize = compactLabelKey ? keyFontSize + 6 : keyFontSize;
  const renderedLabelFontSize = compactLabelKey ? labelFontSize + 8 : labelFontSize;
  // Approximate width needed for lowercase labels (dark/odd/consonant/...) so label text fits.
  const estimatedLabelWidthPx = Math.ceil((labelText?.length ?? 0) * renderedLabelFontSize * 0.62)
    + (labelHorizontalPadding * 2);
  const widthPx = showLabel ? Math.max(width, customLabelWidth ?? estimatedLabelWidthPx) : width;

  return `
    <div style="
      --lane-key-width:${widthPx}px;
      --lane-key-height:${heightPx}px;
      border-radius:12px;
      background:${bgColor};
      border:2px solid ${borderColor};
      box-sizing:border-box;
      display:flex;
      flex-direction:column;
      align-items:center;
      justify-content:center;
      gap:${gap}px;
      flex:0 0 auto;
      ${opacityStyle}
    " class="lane-labels-keycap lane-labels-lane-key" data-key-code="${keyCode}">
      ${showNumber ? `
      <span class="lane-labels-lane-key-number" style="
        --lane-key-number-font-size:${numberFontSize}px;
        --lane-key-number-stroke-width:${numberStrokeWidth}px;
      ">${laneNumber}</span>` : ""}
      ${showLabel ? (customLabelHtml ?? `
      <span class="lane-labels-lane-key-label" style="
        color:${LABEL_TEXT_COLOR};
        font-family:${labelFontFamily};
        font-weight:${labelFontWeight};
        --lane-key-label-font-size:${renderedLabelFontSize}px;
        line-height:1;
        text-transform:lowercase;
      ">${labelText}</span>`) : ""}
      <span class="lane-labels-lane-key-letter" style="
        color:${textColor};
        font-family:'Baloo Paaji 2','Baloo Paaji',sans-serif;
        font-weight:700;
        line-height:1;
        transform:translateY(${letterOffsetY}px);
        --lane-key-font-size:${renderedKeyFontSize}px;
        --lane-key-stroke-width:${keyStrokeWidth}px;
        -webkit-text-stroke-color:${strokeColor};
        paint-order:stroke fill;
        text-shadow:none;
      ">${letter}</span>
    </div>`;
}

function buildKeyRow(keys) {
  return `
    <div class="lane-labels-key-row">${keys.join("")}</div>`;
}

// ─── Variant builders ───────────────────────────────────────────────────────

/** footer_LaneLabelsLearn_Mission1: slowdown + lane keys (key only) + speedup */
function buildLearnMission1(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const laneKeys = LANE_KEY_CODES.map((kc, i) =>
    buildLaneKeyCap(kc, { layoutConfig, showLabel: false, showNumber: false, index: i })
  );
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, ...laneKeys, speedup]);
}

/** footer_LaneLabels: slowdown + lane keys (key + label) + speedup */
function buildLaneLabels(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const laneKeys = LANE_KEY_CODES.map((kc, i) =>
    buildLaneKeyCap(kc, { layoutConfig, showLabel: true, showNumber: false, index: i })
  );
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, ...laneKeys, speedup]);
}

/** footer_LaneLabels_speedUp: slowdown (50% opacity) + speedup */
function buildSpeedUp(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig, { opacity: 0.5 });
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, speedup]);
}

/** footer_LaneLabels_speedUp_reveal: spacebar + speedup */
function buildSpeedUpReveal(layoutConfig) {
  const slowdownPlaceholder = buildSpeedKeyCap("slowdown", layoutConfig, { opacity: 0 });
  const spacebar = buildSpaceBarKeyCap();
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdownPlaceholder, spacebar, speedup]);
}

/** footer_LaneLabels_speedDown: slowdown + speedup (speedup 50% opacity) */
function buildSpeedDown(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const speedup = buildSpeedKeyCap("speedup", layoutConfig, { opacity: 0.5 });
  return buildKeyRow([slowdown, speedup]);
}

/** footer_LaneLabels_spaceBar: spacebar only */
function buildSpaceBar() {
  return buildKeyRow([buildSpaceBarKeyCap()]);
}

/** footer_LaneLabels_M3: slowdown + lane keys (key + label, KeyS shows package icon, ; dimmed) + speedup */
function buildM3(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const laneKeys = LANE_KEY_CODES.map((kc, i) => {
    const dimmed = kc === "Semicolon";
    return buildLaneKeyCap(kc, {
      layoutConfig,
      showLabel: true,
      showNumber: false,
      index: i,
      customLabelHtml: kc === "KeyS"
        ? `<img
        src="${PACKAGE_ICON_SRC}"
        style="
          display: block;
          width: 40px;
          height: 30px;
          object-fit: contain;
          flex-shrink: 0;
          margin-bottom: -5px;
          margin-top: -8px;
        "
        alt="Package"
        />` : null,
      customLabelWidth: kc === "KeyS" ? 62 : null,
      opacity: dimmed ? 0.2 : 1,
    });
  });
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, ...laneKeys, speedup]);
}

/** footer_LaneLabels_M2: slowdown + lane keys (number + key + label) + speedup */
function buildM2(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const laneKeys = LANE_KEY_CODES.map((kc, i) =>
    buildLaneKeyCap(kc, {
      layoutConfig,
      showLabel: true,
      showNumber: true,
      index: i,
      labelFontSize: 18,
      // labelFontWeight: 800,
      keyFontSize: 32,
      numberFontSize: 22,
      numberStrokeWidth: 5,
      labeledHeight: 92,
    })
  );
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, ...laneKeys, speedup]);
}

/** footer_LaneLabels_M5: slowdown + lane keys (key + label) + speedup */
function buildM5(layoutConfig) {
  const slowdown = buildSpeedKeyCap("slowdown", layoutConfig);
  const laneKeys = LANE_KEY_CODES.map((kc, i) =>
    buildLaneKeyCap(kc, {
      layoutConfig,
      showLabel: true,
      showNumber: false,
      index: i,
    })
  );
  const speedup = buildSpeedKeyCap("speedup", layoutConfig);
  return buildKeyRow([slowdown, ...laneKeys, speedup]);
}

/** footer_LaneLabels_M5_roadOnly: lane keys (key + label) only */
function buildM5RoadOnly(layoutConfig) {
  const laneKeys = LANE_KEY_CODES.map((kc, i) =>
    buildLaneKeyCap(kc, {
      layoutConfig,
      showLabel: true,
      showNumber: false,
      index: i,
    })
  );
  return buildKeyRow([...laneKeys]);
}

// ─── Variant registry ────────────────────────────────────────────────────────

const VARIANT_BUILDERS = {
  footer_LaneLabelsLearn_Mission1: buildLearnMission1,
  footer_LaneLabels: buildLaneLabels,
  footer_LaneLabels_speedUp: buildSpeedUp,
  footer_LaneLabels_speedUp_reveal: buildSpeedUpReveal,
  footer_LaneLabels_speedDown: buildSpeedDown,
  footer_LaneLabels_spaceBar: buildSpaceBar,
  footer_LaneLabels_M3: buildM3,
  footer_LaneLabels_M2: buildM2,
  footer_LaneLabels_M5: buildM5,
  footer_LaneLabels_M5_roadOnly: buildM5RoadOnly,
};

const NO_LAYOUT_VARIANTS = new Set(["footer_LaneLabels_spaceBar"]);

function getVariantKey(agentFile) {
  if (!agentFile) return null;
  return String(agentFile).replace(/^.*\//, "").replace(/\.svg$/i, "");
}

export function isFooterLaneLabelVariant(agentFile) {
  return getVariantKey(agentFile) in VARIANT_BUILDERS;
}

export function buildFooterLaneLabelsHTML(jsPsych, agentFile) {
  const variantKey = getVariantKey(agentFile);
  const builder = VARIANT_BUILDERS[variantKey];
  if (!builder) return null;

  if (NO_LAYOUT_VARIANTS.has(variantKey)) {
    return builder();
  }

  const layoutConfig = getSelectedKeyboardLayoutConfig(jsPsych);
  return builder(layoutConfig);
}

export function injectFooterLaneLabels(jsPsych, agentFile) {
  const gameWrapper = document.getElementById("gameWrapper");
  const footerAgent = document.getElementById("footerAgent");
  if (!gameWrapper && !footerAgent) return null;

  removeFooterLaneLabels();

  const html = buildFooterLaneLabelsHTML(jsPsych, agentFile);
  if (!html) return null;

  const temp = document.createElement("div");
  temp.innerHTML = html;
  const row = temp.querySelector(".lane-labels-key-row");

  if (!row) {
    const fallbackHost = gameWrapper || footerAgent;
    if (!fallbackHost) return null;

    const fallbackWrapper = document.createElement("div");
    fallbackWrapper.id = gameWrapper ? "game-lane-labels-panel" : "footer-lane-labels-panel";
    if (gameWrapper) {
      fallbackWrapper.className = "game-lane-labels-panel";
    } else {
      fallbackWrapper.style.cssText =
        "display:flex;align-items:center;justify-content:center;width:100%;height:100%;box-sizing:border-box;";
    }
    fallbackWrapper.innerHTML = html;

    const agentImageFallback = document.getElementById("agentImage");
    if (agentImageFallback) agentImageFallback.style.display = "none";

    fallbackHost.appendChild(fallbackWrapper);
    return fallbackWrapper;
  }

  const keyNodes = [...row.children].filter((node) => node instanceof HTMLElement);
  const laneKeyNodes = keyNodes.filter((node) => node.classList.contains("lane-labels-lane-key"));
  const footerKeyNodes = keyNodes.filter((node) => !node.classList.contains("lane-labels-lane-key"));

  const variantKey = String(agentFile).replace(/^.*\//, "").replace(/\.svg$/i, "");

  let gamePanel = null;
  if (gameWrapper && laneKeyNodes.length > 0) {
    gamePanel = document.createElement("div");
    gamePanel.id = "game-lane-labels-panel";
    gamePanel.className = "game-lane-labels-panel";
    gamePanel.dataset.variant = variantKey;

    const gameRow = document.createElement("div");
    gameRow.className = "lane-labels-key-row lane-labels-key-row--game";
    laneKeyNodes.forEach((node) => gameRow.appendChild(node.cloneNode(true)));
    gamePanel.appendChild(gameRow);
    gameWrapper.appendChild(gamePanel);
  }

  let footerPanel = null;
  if (footerAgent && footerKeyNodes.length > 0) {
    footerPanel = document.createElement("div");
    footerPanel.id = "footer-lane-labels-panel";
    footerPanel.style.cssText =
      "display:flex;align-items:center;justify-content:center;width:100%;height:100%;box-sizing:border-box;";

    const footerRow = document.createElement("div");
    const shouldCenterFooterRow = footerKeyNodes.length === 1;
    footerRow.className = shouldCenterFooterRow
      ? "lane-labels-key-row lane-labels-key-row--footer lane-labels-key-row--footer-center"
      : "lane-labels-key-row lane-labels-key-row--footer";
    footerKeyNodes.forEach((node) => footerRow.appendChild(node.cloneNode(true)));
    footerPanel.appendChild(footerRow);
    footerAgent.appendChild(footerPanel);
  }

  const agentImage = document.getElementById("agentImage");
  if (agentImage) agentImage.style.display = "none";

  return gamePanel || footerPanel;
}

export function removeFooterLaneLabels() {
  const panel = document.getElementById("footer-lane-labels-panel");
  if (panel) panel.remove();

  const gamePanel = document.getElementById("game-lane-labels-panel");
  if (gamePanel) gamePanel.remove();
}
